import React, { useState, useEffect } from "react";
import { LiveStreamBanner } from "./sections/LiveStreamBanner";
import { GreetingSection } from "./sections/GreetingSection";
import { ChallengesSection } from "./sections/ChallengesSection";
import { DailyTasksSection } from "./sections/DailyTasksSection";
import { MetricsSection } from "./sections/MetricsSection";
import { PreChallengeBanner } from "./sections/PreChallengeBanner";
import FirstWeeksCard from "@/components/home/sections/FirstWeeksCard";
import ComebackCard from "@/components/home/sections/ComebackCard";
import { useComebackCard } from "@/hooks/useComebackCard";
import CourseCompletionDialog from "@/components/challenge/CourseCompletionDialog";
import { GracePeriodBanner } from "@/components/challenge/GracePeriodBanner";
import { todayInIsrael } from "@/lib/dateUtils";
import { useChallenge } from "@/contexts/ChallengeContext";

/**
 * App Home – Frameless Flow (RTL)
 * Orchestration component for home page sections
 * Grid spec: outside gutter 32px (px-8), inside gutter 24px (gap-6), 1 column
 * Design goals: Calm & modern, linear progress bars, no cards feeling
 */
function HomeSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" dir="rtl">
      <div className="space-y-2">
        <div className="h-6 w-48 bg-muted/40 rounded" />
        <div className="h-4 w-32 bg-muted/30 rounded" />
      </div>
      <div className="bg-card rounded-2xl p-5 shadow-sm">
        <div className="flex gap-3 items-start">
          <div className="h-11 w-11 rounded-lg bg-muted shrink-0" />
          <div className="flex-1 space-y-3 pt-1">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded" />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="h-5 w-32 bg-muted/40 rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-card rounded-xl shadow-sm" />
        ))}
      </div>
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex-1 aspect-square rounded-2xl bg-card shadow-sm" />
        ))}
      </div>
    </div>
  );
}

export default function AppHomeFramelessFlow() {
  const {
    isLoading,
    isCourseCompleted,
    currentChallengeType,
    graceEndDate,
    gracePeriodDays,
    isPreChallengeState,
    currentChallengeStartDate,
    currentDayData,
    currentChallengeId,
    getCurrentDay,
  } = useChallenge();
  // A hook: must stay above the early `isLoading` return below.
  const comeback = useComebackCard();
  const [showCourseComplete, setShowCourseComplete] = useState(false);

  // Show course completion dialog once when a course is completed
  useEffect(() => {
    if (isCourseCompleted && currentChallengeType === 'course') {
      const dismissed = sessionStorage.getItem('course_complete_dismissed');
      if (!dismissed) {
        setShowCourseComplete(true);
      }
    }
  }, [isCourseCompleted, currentChallengeType]);

  const handleDismissCourseComplete = (open: boolean) => {
    setShowCourseComplete(open);
    if (!open) {
      sessionStorage.setItem('course_complete_dismissed', 'true');
    }
  };

  // Show the grace period banner when the user has completed a course and is within the grace window
  const todayStr = todayInIsrael();
  const showGraceBanner = isCourseCompleted &&
    currentChallengeType === 'course' &&
    graceEndDate !== null &&
    graceEndDate >= todayStr;

  // Gate the entire home page on the challenge context settling so all sections
  // transition together (skeleton → full home) instead of popping in one-by-one
  // as currentChallengeId / currentDayData / unlockedFeatures arrive.
  if (isLoading) {
    return <HomeSkeleton />;
  }

  const hasActiveTimeBasedChallenge = !!currentChallengeType && !isCourseCompleted;

  // Pre-challenge: enrolled but the challenge has not started yet AND there's
  // no preparation-day content available for today. We rely on the absence of
  // `currentDayData` to avoid hiding actual prep-day videos when they exist.
  const inPreChallenge = isPreChallengeState();
  const showPreChallengeBanner =
    hasActiveTimeBasedChallenge &&
    inPreChallenge &&
    !currentDayData &&
    !!currentChallengeStartDate;

  // "טוב שחזרת": only when there is a lesson to continue from today and no
  // other banner is speaking. When it is held back nothing is dismissed, so it
  // can still appear later the same day.
  const currentDay = getCurrentDay();
  const showComeback =
    comeback.visible &&
    !!currentChallengeId &&
    !!currentDayData &&
    currentDay >= 1 &&
    !showGraceBanner &&
    !showPreChallengeBanner;

  return <div>
      <LiveStreamBanner />
      <GreetingSection />
      {showComeback && (
        <ComebackCard
          daysInactive={comeback.daysInactive}
          onDismiss={comeback.dismiss}
          lessonPath={`/challenge/${currentChallengeId}/day/${currentDay}`}
        />
      )}
      {/* Not while the comeback answer is loading: it would mount, log its
          shown event and then be replaced. */}
      {!showComeback && !comeback.pending && <FirstWeeksCard />}
      {showGraceBanner && <GracePeriodBanner graceEndDate={graceEndDate as string} />}
      {showPreChallengeBanner && (
        <PreChallengeBanner startDate={currentChallengeStartDate as string} />
      )}
      <ChallengesSection />
      <DailyTasksSection />
      <MetricsSection />
      <CourseCompletionDialog
        open={showCourseComplete}
        onOpenChange={handleDismissCourseComplete}
        gracePeriodDays={gracePeriodDays}
      />
    </div>;
}

